import StarsBanner from "~/ui/stars-banner";
import FirmLike from "./like";
import { loadClamp } from "~/es6/clamp-loader";
import { MAX_LEN, LOADING_CLASS, THANK_YOU_TO, RULES_LINKS, RULES_LINKS_NOFIRM, HIDE_CLASS, FULLTEXT_REVIEW, FULLTEXT_REVIEW_SOURCE } from "./const";
import FirmReviewRemove from "./remove";
import FirmReviewStarsPicker from "./stars-picker";
import { store } from "~/state/store";
import { node, icon } from "~/util/dom";
import { deferPromise } from "~/util/general";
import { createVerifiedUserTooltip } from "~/util/userweb";
import { CONFIG } from "~/config";
import { dateFormat } from "~/util/format";
import { getFirmLayout, clamp, getFirmImpressConfig, hasFilledStructuredReviews } from "~/util/review";
import { createReviewPhotos } from "~/ui/review/photos";
import { createReviewPhotosUpload } from "~/ui/review/photosupload";
import { createEmojis } from "~/ui/review/emojis";
import { createEmojisPreview } from "~/ui/review/emojisPreview";
import { getImage176x100 } from "~/util/cdn";
import { putReview } from "~/util/providers/review";
import { putToCacheWithoutDetail } from "~/util/reviews-cache";
import { IReview, IReviewItem } from "./interfaces";
import { IDeferPromise, IReviewConf } from "~/interfaces";
import { _ } from "~/i18n";
import ReviewPopup from "./popupwc";

const OPEN_CLASS = "open";
const EDIT_MODE = "edit-mode";

interface IFirmReviewEdit {
	data: IReviewConf;
	isBottom?: boolean;
	isPanel?: boolean;
	logSource?: string;
	analytics?: {[key: string]: string|number|boolean};
	inactive?: boolean;
	thankYouCont?: null | HTMLElement;
	update?: boolean;
	onSave?: (data: { review: IReview; myReview: IReviewItem; }) => void;
	onCancel?: () => void;
	scrollOnEdit?: boolean;
	poi?: {
		source: string;
		id: string | number;
		title: string;
		poitypeId: number;
		mark: {
			lon: number;
			lat: number;
		}
	};
}

// editace - bud zobrazime content pro vypis nebo editaci
export default class FirmReviewEdit {
	_dom: {
		[key: string]: HTMLElement;
		text?: HTMLTextAreaElement;
	};

	_opts: IFirmReviewEdit;

	_isEdit: boolean;

	_myReview: IReviewItem;

	_putReviewId: number | string;

	_logPosition: string;

	_starsPicker: FirmReviewStarsPicker;

	_firstStarsClick: boolean;

	_thankYouID: ReturnType<typeof setTimeout>;

	_firmLike: FirmLike;

	_firmImpressWasSend: boolean;

	_clampIsShortened: boolean;

	_firstClampPromiseData: IDeferPromise;

	_reviewPhotosUpload: ReturnType<typeof createReviewPhotosUpload>;

	_emojis: ReturnType<typeof createEmojis>;

	_keyInputAnalytics: boolean;

	_photosData: {
		logChange: boolean;
		added: number;
		removed: number;
	};

	_wasEmojisChange: boolean;

	constructor(optsArg: IFirmReviewEdit) {
		this._dom = {
			container: node("div", { className: "firm-review-edit" }),
		};
		this._opts = {
			data: null,
			isBottom: false, // pozice, default top
			isPanel: false, // jedna se o panel?
			logSource: "recenze-poi",
			analytics: {},
			inactive: false,
			thankYouCont: null, // container, do ktereho se ma thank you pripnout
			update: false, // jedna se o editaci, nebo aktualizaci
			onSave: () => { /**/ }, // putReviewData data
			onCancel: () => { /**/ },
			scrollOnEdit: true,
			...optsArg,
		};
		this._isEdit = false;
		this._myReview = null;
		this._putReviewId = this._opts.data.id;
		this._logPosition = this._opts.isBottom ? "bottom" : "top";
		this._starsPicker = new FirmReviewStarsPicker({
			starWidth: 48,
			starOffset: 13,
			noTitle: true,
			clickCb: (starsItem, oldStarsItem) => {
				this._starsPickerClick(starsItem, oldStarsItem);
			},
		});
		this._firstStarsClick = true;
		this._thankYouID = null;
		this._firmLike = null;
		this._firmImpressWasSend = false;
		this._clampIsShortened = false;
		this._firstClampPromiseData = deferPromise();
		this._reviewPhotosUpload = null;
		this._emojis = null;
		this._photosData = {
			logChange: false,
			added: 0,
			removed: 0,
		};
		this._wasEmojisChange = false;

		this._build();
		this.setMyReview(this._opts.data.myReview);
	}

	destroy() {
		this._dom.container.remove();
		this._reviewPhotosUpload.destroy();
	}

	get container() {
		return this._dom.container;
	}

	get header() {
		return this._dom.header;
	}

	openEdit() {
		this._openEdit();
	}

	setMyReview(data: IReviewItem) {
		this._myReview = data ? { ...data } : null;

		this._update();
	}

	handleEvent(event: Event) {
		if (event.type === "pointerdown") {
			if (event.target !== this._dom.verticalIcon && !this._dom.optsList.contains(event.target as HTMLElement)) {
				this._closeOpts();
			}
		}
	}

	firmImpressAnalytics() {
		if (this._firmImpressWasSend || !this._myReview) {
			return true;
		}

		this._firmImpressAnalytics();

		this._firmImpressWasSend = true;

		return false;
	}

	_firmImpressAnalytics(reviewShowMoreAction = false) {
		this._firstClampPromiseData.promise.then(() => {
			const config = getFirmImpressConfig({
				reviewId: this._putReviewId,
				state: this._myReview.state,
				reviewOrder: 1,
				reviewShortened: reviewShowMoreAction || this._clampIsShortened,
				reviewAuthor: true,
				reviewShowMoreAction,
			});

			Mapy.getComponent("logger").log(config.action, config.data);
		}, () => { /**/ });
	}

	_build() {
		const reviewArea = node("div", { className: "review-area" });
		const text = node("textarea", { className: "text", placeholder: _("detail.content.review2.writePlaceholder") });

		text.setAttribute("maxlength", MAX_LEN.toString());
		text.addEventListener("input", () => {
			this._updateRest(true);
		});

		const counter = node("p", { className: "counter" });

		reviewArea.appendChild(text);
		reviewArea.appendChild(counter);

		this._reviewPhotosUpload = createReviewPhotosUpload({
			onUpdate: data => {
				this._dom.errorInfo.textContent = "";

				if (data.loading) {
					this._dom.buttons.classList.add("photos-uploading");
				} else {
					this._dom.buttons.classList.remove("photos-uploading");
				}

				if (data.type === "upload-file") {
					this._photosData.added++;
				} else if (data.type === "remove") {
					this._photosData.removed++;
				}

				if (this._photosData.logChange && data.type === "add-files") {
					this._log("photoEdit");
					this._photosData.logChange = false;
				}
			},
			onError: data => {
				this._dom.errorInfo.textContent = data.msg;
			},
		});
		reviewArea.append(this._reviewPhotosUpload.container);

		const lang = CONFIG.defaultLang;
		const rulesSource = this._opts.data.isFirm ? RULES_LINKS : RULES_LINKS_NOFIRM;
		const agreement = node("p", {
			className: "licence",
			innerHTML: _("detail.content.review2.acceptLicence2", {
				rules: Object.keys(rulesSource).indexOf(lang) > -1 ? rulesSource[lang] : rulesSource.en,
				terms: `https://licence.mapy.${CONFIG.TLD}/?doc=mapy_lu&lang=${lang}`,
			}),
		});
		const linksEl = Array.from(agreement.querySelectorAll("a"));

		linksEl.forEach((item, ind) => {
			item.setAttribute("data-log-ignore", "true");
			item.addEventListener("pointerdown", () => {
				this._log("click", {
					url: item.href,
					text: ind === 0 ? "podmínkami" : "pravidly",
				});
			});
		});

		reviewArea.appendChild(agreement);
		const buttons = node("div", { className: "buttons" });
		const saveBtn = node("button", { className: "save-btn", textContent: _("detail.content.review.submit") });

		saveBtn.setAttribute("data-log-ignore", "");
		saveBtn.addEventListener("click", event => {
			event.stopPropagation();
			event.preventDefault();
			this._save();
		});

		const loaderImg = node("img", { src: "/img/loader.gif", className: "photos-loader" });

		buttons.appendChild(loaderImg);
		const cancelBtn = node("button", { className: "cancel-btn", textContent: _("cancel") });

		cancelBtn.setAttribute("data-log-ignore", "");
		cancelBtn.addEventListener("click", event => {
			event.stopPropagation();
			event.preventDefault();
			// analytika
			this._log("click", {
				text: "Zrušit",
			});
			this._cancel(true);
		});
		buttons.appendChild(saveBtn);
		buttons.appendChild(loaderImg);
		buttons.appendChild(cancelBtn);
		reviewArea.appendChild(buttons);
		const errorInfo = node("p", { className: "error" });

		reviewArea.appendChild(errorInfo);
		// hodnceni
		const reviewCont = node("div", { className: "review-cont" });
		const header = node("a", { className: "header-part", href: `https://${CONFIG.BASE_URL}/?moje-mapy&cat=reviews` });
		const { user } = store.getState().user;
		const imgHolder = node("mapy-avatar", { className: "img-holder", size: 40, premium: Mapy.CUS.user.premiumCrowns && user.premium ? "mapy" : "none" });

		if (user.avatar) {
			const userBadge = node("img", { src: user.avatar });

			userBadge.crossOrigin = "anonymous";
			imgHolder.appendChild(userBadge);
		} else {
			imgHolder.classList.add("svg-avatar");
			imgHolder.appendChild(icon("avatar-anonym"));
		}

		header.appendChild(imgHolder);
		const infoPart = node("div", { className: "info-part" });

		header.appendChild(infoPart);
		reviewCont.appendChild(header);
		const title = node("h2");

		title.appendChild(node("div", {}, (user.name || user.accountDisplayName.replace(/[@].*$/u, "")).trim()));

		if (user.bankId) {
			const verifiedIcon = icon("verifieduser16");

			title.appendChild(verifiedIcon);
			createVerifiedUserTooltip(verifiedIcon);
		}

		infoPart.appendChild(title);
		// stars count
		const ratingStars = node("span", { className: "stars-placeholder" });
		// date
		const date = node("span", { className: "date" });
		const allReviews = node("p", { className: `all-reviews`, textContent: _("detail.content.booking.reviewCount2", { count: this._opts?.data?.myReview?.user?.review_count || 0 }) });

		allReviews.appendChild(icon("forward"));
		infoPart.appendChild(allReviews);
		// hodnoceni a datum
		const starsWithDate = node("div", { className: "stars-with-date" });

		starsWithDate.appendChild(ratingStars);
		starsWithDate.appendChild(date);
		reviewCont.appendChild(starsWithDate);
		const optsArea = node("span", { className: "opts-area" });
		const verticalIcon = icon("etc-vertical");
		const optsList = node("span", { className: "list" });
		const editBtn = node("button", { className: "list-edit-btn" });

		editBtn.setAttribute("data-log-ignore", "");
		editBtn.appendChild(icon("edit"));
		const editBtnSpan = node("span");

		editBtn.appendChild(editBtnSpan);
		const removeBtn = node("button", { className: "list-remove-btn" });

		removeBtn.setAttribute("data-log-ignore", "");
		removeBtn.appendChild(icon("delete"));
		removeBtn.appendChild(node("span", { textContent: _("delete") }));

		optsArea.appendChild(verticalIcon);
		optsArea.appendChild(optsList);
		editBtn.addEventListener("click", () => {
			this._closeOpts();
			// analytika
			this._log("click", {
				text: this._myReview.actual === false ? "Aktualizovat hodnocení" : "Upravit hodnocení",
			});
			this._openEdit();
			this._dom.text.focus();
		});
		removeBtn.addEventListener("click", () => {
			this._closeOpts();
			// analytika
			this._log("click", {
				text: "Smazat",
			});

			this._openRemovePopup();
		});
		verticalIcon.addEventListener("click", event => {
			event.preventDefault();
			event.stopPropagation();
			// analytika
			this._log("click", {
				text: "contextMenu",
				section: "reviews",
			});
			optsArea.classList.toggle(OPEN_CLASS);

			if (optsArea.classList.contains(OPEN_CLASS)) {
				document.addEventListener("pointerdown", this);
			} else {
				this._closeOpts();
			}
		});

		header.addEventListener("click", event => {
			if (Mapy.Util.linkToNewWindow(event)) {
				return;
			}

			event.preventDefault();

			if (!optsList.contains(event.target as HTMLElement)) {
				Mapy.getComponent("mymaps").deactivate();
				Mapy.getComponent("mymaps").activate();
				Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("reviews"));

				setTimeout(() => {
					Mapy.getComponent("history").save();
				}, 0);
			}
		});

		if (!this._opts.inactive) {
			infoPart.appendChild(optsArea);
		}

		const myReviewWeight = node("p", { style: "font-style: italic; color: gray; display: none" });
		const reviewDesc = node("p");
		// strukturovane hodnoceni
		const strRatingCont = node("div", { className: "str-rating-cont" });
		// fotky
		const photosCont = node("div", { className: "photos-cont" });
		// like vlastni recenze
		const likeCont = node("div", { className: "like-cont" });

		reviewCont.append(
			myReviewWeight,
			reviewDesc,
			strRatingCont,
			photosCont,
			likeCont,
		);

		const rejectedCont = node("div", { className: "rejected-cont" });
		// nova hlavicka
		const reviewHeader = node("div", { className: "review-header" });
		const reviewEmojiPlaceholder = node("div", { className: "review-emoji-placeholder" });
		const captionLine1 = node("p", { className: "caption" });
		const captionLine2 = node("p", { className: "caption" });

		reviewHeader.append(
			captionLine1,
			captionLine2,
			reviewEmojiPlaceholder,
			this._starsPicker.container,
		);
		this._dom.container.append(
			reviewHeader,
			reviewArea,
			reviewCont,
			rejectedCont,
		);
		Object.assign(this._dom, {
			reviewArea,
			reviewCont,
			text,
			counter,
			reviewDesc,
			captionLine1,
			captionLine2,
			ratingStars,
			date,
			saveBtn,
			errorInfo,
			myReviewWeight,
			optsArea,
			verticalIcon,
			optsList,
			editBtnSpan,
			likeCont,
			header,
			rejectedCont,
			buttons,
			strRatingCont,
			photosCont,
			editBtn,
			removeBtn,
			reviewHeader,
			reviewEmojiPlaceholder,
		});
	}

	_update() {
		this._dom.editBtnSpan.textContent = this._myReview?.actual === false ? _("update") : _("edit");
		this._dom.editBtn.remove();
		this._dom.removeBtn.remove();

		if (!this._myReview) {
			return;
		}

		const date = new Date(this._myReview.updated);
		const createDate = dateFormat(date, "d. mn yyyy");
		const { user } = store.getState().user;

		// update hlavicky
		this._dom.captionLine1.textContent = `${_("detail.content.review2.editFrom", { date: createDate })}:`;
		this._dom.captionLine2.innerHTML = `${_("detail.content.review2.reviewAsUser", { user: `<strong>${Mapy.Util.escapeHTML(user.name)}</strong>` })}`;

		// overeny uzivatel
		if (user.bankId) {
			const verifiedIcon = icon("verifieduser16");

			createVerifiedUserTooltip(verifiedIcon);
			this._dom.captionLine2.appendChild(verifiedIcon);
		}

		this._dom.saveBtn.textContent = _("detail.content.review.submit");
		this._dom.reviewDesc.innerHTML = Mapy.Util.escapeHTML(this._myReview.text || "");
		this._dom.date.textContent = createDate;
		this._dom.text.value = this._myReview.text || "";
		this._starsPicker.percent = this._myReview.rating;
		this._dom.strRatingCont.innerHTML = "";
		this._dom.photosCont.innerHTML = "";
		this._dom.rejectedCont.innerHTML = "";
		this._dom.reviewEmojiPlaceholder.innerHTML = "";

		if (Mapy.CUS.userweb && Mapy.CUS.userweb.firmDebug && typeof this._myReview.weight === "number") {
			this._dom.myReviewWeight.innerHTML = `Celková váha: <strong>${(this._myReview.weight || 0).toFixed(3)}`;
			this._dom.myReviewWeight.style.display = "block";
		}

		this._updateLittleStars();
		this._updateRest();

		// dom
		this._dom.reviewHeader.classList.add(HIDE_CLASS);
		this._dom.reviewArea.classList.add(HIDE_CLASS);
		this._dom.reviewCont.classList.add(HIDE_CLASS);

		this._starsPicker.setDefaultTitle();

		if (this._dom.timeCover) {
			this._dom.timeCover.remove();
			this._dom.timeCover = null;
		}

		if (this._myReview.enableEdit === true) {
			this._dom.optsList.appendChild(this._dom.editBtn);
		}

		this._dom.optsList.appendChild(this._dom.removeBtn);

		if (!this._firmLike && (this._myReview.text?.length || (Array.isArray(this._myReview.gallery) && this._myReview.gallery.length))) {
			const { data } = store.getState().detail;

			this._firmLike = new FirmLike({
				id: this._myReview.likes ? this._myReview.likes.id : `review.firm.${this._myReview.id}`,
				data: this._myReview.likes,
				disabled: true,
				detail: {
					source: data?.source,
					id: data?.id as number,
					typeId: data?.typeId,
				},
			});
			this._dom.likeCont.appendChild(this._firmLike.container);
		}

		if (this._isEdit) {
			this._dom.reviewHeader.classList.remove(HIDE_CLASS);
			this._dom.reviewArea.classList.remove(HIDE_CLASS);
			this._dom.saveBtn.textContent = _("action.save.changeTitle");
			this._starsPicker.title = `<strong>${_(this._opts.update ? "detail.content.review2.updateFrom" : "detail.content.review2.editFrom", { date: createDate })}</strong>`;

			// fotky
			const photos = Array.isArray(this._myReview.gallery)
				? this._myReview.gallery.map(photo => ({
					mediaId: photo.id,
					rejected: photo.status === "rejected",
					src: getImage176x100(photo.urls?.default || ""),
				}))
				: [];

			// reset pro fotky
			this._photosData.logChange = false;
			this._photosData.added = 0;
			this._photosData.removed = 0;

			this._reviewPhotosUpload.addPhotos(photos, true).then(() => {
				this._photosData.logChange = true;
			}, () => { /**/ });

			this._wasEmojisChange = false;

			// emoji
			if (Array.isArray(this._myReview.structured_rating) && this._myReview.structured_rating.length) {
				const emojis = createEmojis({
					config: this._myReview.structured_rating,
					variant: "new",
					buildTooltip: false,
					onUpdate: () => {
						if (!this._wasEmojisChange) {
							this._log("click", {
								text: "attributesEdit",
							});
							this._wasEmojisChange = true;
						}
					},
				});

				this._dom.reviewEmojiPlaceholder.append(emojis.container);
				this._emojis = emojis;
			}
		} else {
			this._dom.reviewCont.classList.remove(HIDE_CLASS);

			if (this._myReview.state === "waiting") {
				const timeCover = node("span", { className: "time-cover" });
				const timeSvg = icon("time");
				const timeCaption = node("span", { className: "time-caption", textContent: _("detail.content.review2.acceptWaiting") });

				timeCover.appendChild(timeSvg);
				timeCover.appendChild(timeCaption);
				this._dom.optsArea.insertBefore(timeCover, this._dom.optsArea.firstChild);
				this._dom.timeCover = timeCover;
			} else if (this._myReview.state === "rejected") {
				const msg = node("p", { textContent: this._myReview.rejectReason });
				const buttons = node("div", { className: "rejected-buttons" });

				if (this._myReview.enableEdit === true) {
					const editTextBtn = node("button", { className: "edit-text-btn", textContent: _("edit") });

					editTextBtn.addEventListener("click", () => {
						this._openEdit();
					});
					buttons.append(editTextBtn);
				}

				const removeReviewBtn = node("button", { className: "remove-review-btn", textContent: _("detail.content.review2.removeReview") });

				removeReviewBtn.addEventListener("click", () => {
					this._openRemovePopup();
				});
				buttons.append(removeReviewBtn);

				this._dom.rejectedCont.append(msg, buttons);
			}

			// strukturovane hodnoceni
			this._dom.strRatingCont.append(
				createEmojisPreview({
					config: this._myReview.structured_rating || [],
				}).container,
			);

			Array.isArray(this._myReview.gallery) && this._dom.photosCont.append(createReviewPhotos({
				reviewData: {
					...this._myReview,
					...this._opts.poi ? { poi: this._opts.poi } : {},
				},
			}));
		}

		// clampneme text
		loadClamp().then(() => {
			this._clampIsShortened = clamp(this._dom.reviewDesc, true, () => {
				this._firmImpressAnalytics(true);
			});

			this._firstClampPromiseData.resolve({});
		}, () => { /**/ });
	}

	_updateLittleStars() {
		this._dom.ratingStars.innerHTML = "";

		const starsBanner = new StarsBanner({
			percentage: this._starsPicker.starsItem.percent,
		});

		this._dom.ratingStars.appendChild(starsBanner.container);
	}

	_updateRest(logAnalytics?: boolean) {
		const chars = MAX_LEN - this._dom.text.value.length;

		this._dom.counter.innerHTML = _("detail.content.review.numOfChar", {
			chars,
		});

		if (logAnalytics && !this._keyInputAnalytics && chars > 0) {
			// analytika
			this._log("text_fill");
			this._keyInputAnalytics = true;
		}
	}

	_save() {
		const text = this._dom.text.value.trim();

		this._dom.reviewDesc.textContent = text;
		this._dom.date.textContent = dateFormat(new Date(), "dd. mn yyyy");

		// analytika - pred
		this._log("click", {
			text: this._myReview ? "Uložit změny" : "Uložit hodnocení",
		});

		this._dom.container.classList.add(LOADING_CLASS);

		const licence = Mapy.getComponent("licence").getApprove("mapy_lu");

		if (!licence || !licence[0]) {
			Mapy.getComponent("licence").setApprove("mapy_lu", true);
		}

		this._dom.errorInfo.textContent = "";

		putReview({
			source: this._opts.data.source,
			...this._opts.logSource === FULLTEXT_REVIEW
				? {
					reviewSource: FULLTEXT_REVIEW_SOURCE,
				}
				: {},
			poiId: this._opts.data.id,
			text,
			mediaIds: this._reviewPhotosUpload.getMediaIds(),
			structuredRating: this._emojis ? this._emojis.getData() : [],
			rating: this._starsPicker.percent,
		}).then(putReviewData => {
			this._putReviewResponse(putReviewData);
			this._createThankYou();
		}, () => {
			this._dom.errorInfo.textContent = _("detail.content.review2.saveError2");
			this._dom.container.classList.remove(LOADING_CLASS);
		});
	}

	_putReviewResponse(putReviewData) {
		// pokud je cas mensi jak 24h pri editaci, tak se pouzije puvodni datum
		const date = new Date(this._myReview.updated);
		let editDate = date;

		if (date.getTime() + (24 * 3600 * 1000) <= Date.now()) {
			editDate = new Date();
		}

		const review = {
			...this._opts.data.review,
			...putReviewData.review,
		};

		this._myReview = putReviewData.myReview;
		this._myReview.updated = editDate.toString();
		this._dom.errorInfo.textContent = "";
		this._dom.container.classList.remove(LOADING_CLASS);

		// jestlize jsme editovali recenzi, tak znovu nastavime ty data z BE
		if (this._firmLike) {
			this._firmLike.data = this._myReview.likes;
		}

		// analytika - po
		this._log("recenze-leti", {
			attributesEdit: this._wasEmojisChange ? 1 : 0,
			photoCount: this._photosData.added - this._photosData.removed,
		});
		this._closeEdit();

		// ulozime do kese
		putToCacheWithoutDetail(putReviewData, this._opts.data.source, this._opts.data.id);

		// data pri ulozeni
		this._opts.onSave({
			review: review,
			myReview: putReviewData.myReview,
		});
	}

	_createThankYou() {
		if (this._thankYouID) {
			clearTimeout(this._thankYouID);
			this._thankYouID = null;
		}

		if (this._dom.thankYouEl) {
			this._dom.thankYouEl.remove();
			this._dom.thankYouEl = null;
		}

		const thankYouEl = node("div", { className: "firm-edit-thank-you" });

		thankYouEl.appendChild(icon("love"));
		thankYouEl.appendChild(node("span", { textContent: _("detail.content.review2.thankYou") }));

		// kam pripnout?
		if (this._opts.thankYouCont) {
			this._opts.thankYouCont.appendChild(thankYouEl);
		} else {
			this._dom.container.before(thankYouEl);
		}

		this._dom.thankYouEl = thankYouEl;

		this._thankYouID = setTimeout(() => {
			this._thankYouID = null;

			if (this._dom.thankYouEl) {
				this._dom.thankYouEl.remove();
				this._dom.thankYouEl = null;
			}
		}, THANK_YOU_TO);
	}

	_starsPickerClick(starsItem, oldStarsItem) {
		if (!this._isEdit) {
			// analytika - pouze pokud neni editace
			this._log("click", {
				text: "recenze-hvezdy",
			});
		}

		const starsCount = ((starsItem.stars - 1) * 2) + (starsItem.full ? 2 : 1);

		// analytika hvezd
		this._log("click", {
			text: this._firstStarsClick ? `stars-set-${starsCount}` : `stars-updated-${starsCount}`,
		});

		if (this._firstStarsClick) {
			this._firstStarsClick = false;
		}

		this._starsPicker.save(oldStarsItem);

		if (!this._isEdit) {
			this._openEdit(true);
			this._update();
		}
	}

	_cancel(withReset?: boolean) {
		this._firstStarsClick = true;
		this._keyInputAnalytics = false;

		this._updateRest();
		this._closeEdit(withReset);
		this._opts.onCancel();
	}

	_openEdit(noUpdate?: boolean) {
		if (this._isEdit) {
			return;
		}

		if (Mapy.CUS.review?.webcomponent) {
			// eslint-disable-next-line no-new
			new ReviewPopup({
				data: {
					...this._opts.data,
					myReview: this._myReview,
					review: {
						...this._opts.data.review,
						alternatives: [],
					},
				},
				logSource: this._opts.logSource,
				logPosition: "edit",
				fromOtherPOI: false,
				onClose: closeData => {
					if (closeData?.myReview) {
						this._myReview = closeData.myReview;
					}

					this._update();

					this._opts.onSave({
						review: closeData.review,
						myReview: closeData.myReview,
					});
				},
			});
		} else {
			this._dom.container.classList.add(EDIT_MODE);
			this._isEdit = true;

			if (!noUpdate) {
				this._update();
			}

			this._opts.scrollOnEdit && this._dom.container.scrollIntoView();
			this._dom.text.focus();
		}
	}

	_closeEdit(withReset?: boolean) {
		if (!this._isEdit) {
			return;
		}

		this._isEdit = false;
		this._dom.container.classList.remove(EDIT_MODE);

		if (withReset) {
			this._starsPicker.load();
		}

		this._update();
	}

	_closeOpts() {
		this._dom.optsArea.classList.remove(OPEN_CLASS);
		document.removeEventListener("pointerdown", this);
	}

	_openRemovePopup() {
		/* eslint-disable no-new */
		new FirmReviewRemove({
			data: this._opts.data,
			myReview: this._myReview,
			logSource: this._opts.logSource,
			analytics: {
				...this._opts.analytics,
				photos: this._myReview && Array.isArray(this._myReview.gallery) && this._myReview.gallery.length > 0 ? 1 : 0,
				attributes: hasFilledStructuredReviews(this._myReview.structured_rating),
			},
			logPosition: this._logPosition,
		});
	}

	_log(name: string, data?: object) {
		/* eslint-disable camelcase */
		Mapy.getComponent("logger").log(name, {
			position: this._logPosition,
			rating_layout: getFirmLayout(this._opts.data.review, this._opts.data.booking, this._myReview, this._opts.data.isFirm),
			source: this._opts.logSource,
			refused: this._myReview?.state === "rejected" ? 1 : 0,
			type: this._myReview?.actual === false ? "recenze-update" : "recenze-edit",
			sourcePoi: this._opts.data.source,
			typeid: this._opts.data.typeId,
			firmTwin: this._opts.data.isTwin ? 1 : 0,
			section: "reviews",
			...this._opts.analytics,
			...data,
		});
	}
}
